import React from "react";
import { Box, useTheme, useMediaQuery } from "@mui/material";

interface MapProps {
  address: string;
  height?: number | string;
}

function Map({ address, height }: MapProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));
  const isLarge = useMediaQuery(theme.breakpoints.up("lg"));

  const search = `https://maps.google.com/maps?q=${address}&t=&z=12&ie=UTF8&iwloc=&output=embed`;

  // Dynamic dimensions based on viewport - increased sizes
  const getDynamicDimensions = () => {
    if (height) return { height };

    if (isMobile) {
      return {
        height: "350px",
        aspectRatio: "16 / 10", // Slightly wider on mobile for better visibility
        maxHeight: "400px",
        minHeight: "300px",
      };
    } else if (isTablet) {
      return {
        height: "450px",
        aspectRatio: "4 / 3", // More rectangular on tablet
        maxHeight: "500px",
        minHeight: "400px",
      };
    } else if (isLarge) {
      return {
        height: "550px",
        aspectRatio: "1 / 1", // Square on large screens
        maxHeight: "650px",
        minHeight: "500px",
      };
    } else {
      // Default desktop
      return {
        height: "480px",
        aspectRatio: "1 / 1", // Square aspect ratio
        maxHeight: "550px",
        minHeight: "450px",
      };
    }
  };

  return (
    <Box
      sx={{
        width: "100%",
        borderRadius: 1,
        overflow: "hidden",
        "& iframe": {
          borderRadius: 1,
        },
        // Responsive dimensions
        ...getDynamicDimensions(),
        // Additional responsive styles - center on all screen sizes
        mx: "auto", // Center horizontally
        [theme.breakpoints.up("md")]: {
          // Maintain aspect ratio on larger screens
          "&::before": {
            content: '""',
            display: "block",
            paddingTop: isMobile ? "62.5%" : isTablet ? "75%" : "100%", // 16:10, 4:3, 1:1 ratios
          },
          position: "relative",
          "& iframe": {
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
          },
        },
      }}
    >
      <iframe
        title={address}
        src={search}
        width="100%"
        height="100%"
        style={{ border: "none" }}
        loading="lazy"
        referrerPolicy="no-referrer-when-downgrade"
      />
    </Box>
  );
}

export default Map;
